import { clsx } from 'clsx'
import { type ComponentProps, type FC } from 'react'
import { graphql, useFragment } from 'react-relay'

import { type ProfileAvatar_profile$key } from '@/__generated__/ProfileAvatar_profile.graphql'
import placeholder from '@/assets/images/placeholder.png'
import { Avatar, AvatarBadge, AvatarFallbackText, AvatarImage } from '@/components'
import { getPublicImageUrl } from '@/lib'

export interface ProfileAvatarProps extends ComponentProps<typeof Avatar> {
  profileRef: ProfileAvatar_profile$key | null | undefined
  isOnline?: boolean
  showOnline?: boolean
}

export const ProfileAvatar: FC<ProfileAvatarProps> = ({ className, isOnline, profileRef, showOnline, ...props }) => {
  const profileData = useFragment(
    graphql`
      fragment ProfileAvatar_profile on Profile {
        headshot {
          storageObject {
            blurhash
            bucket
            key
          }
        }
        name
      }
    `,
    profileRef
  )
  const blurhash = profileData?.headshot?.storageObject?.blurhash
  const uri = getPublicImageUrl(profileData?.headshot?.storageObject?.bucket, profileData?.headshot?.storageObject?.key)

  return (
    <Avatar
      accessibilityElementsHidden
      aria-hidden
      className={clsx('border-[0.5px] border-white', className)}
      importantForAccessibility='no-hide-descendants'
      {...props}
    >
      {!uri && <AvatarFallbackText>{profileData?.name}</AvatarFallbackText>}
      <AvatarImage
        cachePolicy='memory-disk'
        contentFit='cover'
        placeholder={blurhash ? { blurhash } : placeholder}
        placeholderContentFit='cover'
        source={
          // we are supplying the placeholder image if the profile image isn't there, because the AvatarImage component
          // skips rendering the image completely if there's no source.uri or it is empty
          uri ? { uri } : placeholder
        }
      />
      {showOnline ? (
        <AvatarBadge
          className={clsx('border-[0.5px] border-white bg-gray-400', isOnline && 'bg-green-400')}
          size='sm'
        />
      ) : null}
    </Avatar>
  )
}
